import React, { useEffect, useState } from "react";
import { SovosGrid, SovosTypography, SovosButton, SovosDivider } from "mosaic-react";
import { Add, Delete } from "mosaic-react-icons";
import { DataGrid, DragAndDropInfo } from "colbi_web_ui/lib/components";
import Dropzone from "./Dropzone";
import I18n from "../../I18n";
import { cardGeneration, headerGeneration } from "./utils";
import TranslationTableDetailsCreator from "./TranslationTableDetailsCreator";
import SovosBox from "mosaic-react/sovos-box";
import { handleDeleteTranslationDetailsRow } from "../../actions/UserActions/DatagridHandleUserActions";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import OtherEntitiesDialog from './OtherEntitiesDialog';

const TranslationTableDetailsManagement = (props) => {
    const { items, header: headerItems, translationTableInfo, cdmNames, cdmEntity, datasetLocale, ...rest } = props;

    const { i18n, deleteTranslationDetailsRowAction, pushDialog, pushNotification, reload, getTranslationBuilderCombinationsStats, applyRelatedEntitiesAction } =
        useGlobalValue();

    const [tableData, setTableData] = useState([]);
    const [editOpen, setEditOpen] = useState({ status: false });
    const [isRelatedEntitiesOpen, setIsRelatedEntitiesOpen] = useState(false);
    const [relatedEntities, setRelatedEntities] = useState([]);
    const header = headerGeneration(I18n);
    const { entity, id: translationId, delimiter, organizationId } = translationTableInfo || {};
    const [rowIdUpdate, setRowIdUpdate] = useState(null);

    useEffect(() => {
        const itemsWithCard = (items || []).map((elem) => {
            const card = cardGeneration(
                elem,
                I18n,
                editOpen,
                setEditOpen,
                setRowIdUpdate,
                cdmNames,
                cdmEntity
            );
            return {
                ...elem,
                card,
            };
        });
        setTableData(itemsWithCard);
    }, [items, headerItems, entity, editOpen, cdmNames, cdmEntity]);

    const handleSaveSuccess = (result) => {
        if (result?.update_translation_builder?.success) {
            pushNotification({
                open: true,
                message: `Your changes have been successfully saved.`,
                type: "confirmation",
                timeout: "short",
            });
            reload();
        }
    };

    return (
        <>
            <SovosDivider />
            <SovosGrid
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingTop: "10px",
                    paddingBottom: "10px",
                }}
            >
                <SovosTypography variant="h3">{I18n.translate`Table Builder`}</SovosTypography>
                <SovosBox>
                    <SovosButton
                        variant="text"
                        onClick={async () => {
                            const entitiesByCombination = await getTranslationBuilderCombinationsStats(organizationId, datasetLocale, translationId)
                            setRelatedEntities(entitiesByCombination)
                            setIsRelatedEntitiesOpen(true)
                        }}
                        startIcon={<Add />}
                    >
                        {I18n.translate`Related Entities`}
                    </SovosButton>
                    <SovosButton
                        variant="text"
                        onClick={() => {
                            setEditOpen({
                                status: true,
                                row: {
                                    conditionsParsed: [],
                                    updatesParsed: [],
                                    translationTableBuilderId: null,
                                    translationId,
                                    related_entities: JSON.stringify([cdmEntity]),
                                },
                                cdmNames,
                            });
                        }}
                        startIcon={<Add />}
                    >
                        {I18n.translate`Add row`}
                    </SovosButton>
                    <SovosButton
                        variant="text"
                        onClick={() => {
                            handleDeleteTranslationDetailsRow(
                                {
                                    deleteTranslationDetailsRowAction,
                                    id: "",
                                    translationId,
                                    pushDialog,
                                    i18n,
                                    reload,
                                    deleteAll: true,
                                },
                                pushNotification,
                            );
                        }}
                        startIcon={<Delete />}
                    >
                        {I18n.translate`Delete all rows`}
                    </SovosButton>
                </SovosBox>
            </SovosGrid>
            <DragAndDropInfo csvDelimiter={delimiter} />
            <Dropzone
                Children={DataGrid}
                childrenProps={{
                    ...rest,
                    header,
                    items: tableData,
                }}
                translationId={translationId}
            />
            <TranslationTableDetailsCreator
                editOpen={editOpen}
                setEditOpen={setEditOpen}
                rowIdUpdate={rowIdUpdate}
                translationId={translationId}
                onSaveSuccess={handleSaveSuccess}
                i18n={i18n}
            />
            <OtherEntitiesDialog
                isOpen={isRelatedEntitiesOpen}
                setIsOpen={setIsRelatedEntitiesOpen}
                otherEntities={relatedEntities}
                translationId={translationId}
                message={"consider adding translations"}
                applyRelatedEntitiesAction={applyRelatedEntitiesAction}
                organizationId={organizationId}
            />
        </>
    );
};

export default TranslationTableDetailsManagement;
